import { ChannelDoc, ChannelMessageDoc, ChannelMode } from '@gooduncles/gu-app-schema';
import { Select } from 'antd';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { RangeEventValue, dayjsesToStringDateWhere, getDefaultDates } from 'src/utils/date-util';
import { WHERE } from 'src/utils/firebase/firebase-manager';
import {
  observeChannels,
  observeNoTriggerChannelMessages,
  observeTriggerChannelMessages,
} from 'src/utils/firebase/firebase-short-cut';

import TableOptions from 'src/components/Common/TableOptions/TableOptions';

import classes from './MessageHistory.module.scss';

import FcmStatusPanel from './FcmStatusPanel';
import MessageTable from './MessageTable';

export type MessageRowData = ChannelMessageDoc & {
  source: 'trigger' | 'noTrigger';
};

interface MessageHistoryProps {
  mode: ChannelMode;
}

const fallbackDates = getDefaultDates(3, 3);

const MessageHistory: FC<MessageHistoryProps> = ({ mode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState<MessageRowData[]>([]);
  const [channels, setChannels] = useState<ChannelDoc[]>([]);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const selectedChannelId = searchParams.get('channelId') || null;

  const dates = useMemo<[dayjs.Dayjs, dayjs.Dayjs]>(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to) {
      const f = dayjs(from);
      const t = dayjs(to);
      if (f.isValid() && t.isValid()) return [f, t];
    }
    return fallbackDates;
  }, [searchParams]);

  const [startValue, endValue] = useMemo(() => dayjsesToStringDateWhere(dates[0], dates[1]), [dates]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleDatesChange = useCallback(
    (val: RangeEventValue) => {
      if (val?.[0] && val?.[1]) {
        updateSearchParams({
          from: val[0].format('YYYY-MM-DD'),
          to: val[1].format('YYYY-MM-DD'),
        });
      }
    },
    [updateSearchParams],
  );

  const handleChannelChange = useCallback(
    (v: string) => {
      updateSearchParams({ channelId: v || null });
    },
    [updateSearchParams],
  );

  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      prevModeRef.current = mode;
      updateSearchParams({ channelId: null });
    }
    const subscription = observeChannels([['mode', '==', mode]]).subscribe({
      next: (data) => setChannels(data),
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const channelIds = useMemo(() => new Set(channels.map((ch) => ch._id)), [channels]);

  useEffect(() => {
    if (channelIds.size === 0) {
      setRowData([]);
      return;
    }

    const queryOptions = {
      sortKey: 'createdAt',
      orderBy: 'desc' as const,
      startValue,
      endValue,
    };

    const channelWhere: WHERE[] = selectedChannelId ? [['channelId', '==', selectedChannelId]] : [];

    const filterByMode = (msgs: MessageRowData[]) =>
      selectedChannelId ? msgs : msgs.filter((m) => channelIds.has(m.channelId));

    const subscription = combineLatest([
      observeTriggerChannelMessages(channelWhere, queryOptions).pipe(
        map((msgs) => msgs.map((m) => ({ ...m, source: 'trigger' as const }))),
      ),
      observeNoTriggerChannelMessages(channelWhere, queryOptions).pipe(
        map((msgs) => msgs.map((m) => ({ ...m, source: 'noTrigger' as const }))),
      ),
    ])
      .pipe(
        map(([trigger, noTrigger]) =>
          filterByMode([...trigger, ...noTrigger]).sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
        ),
      )
      .subscribe({
        next: (data) => setRowData(data),
      });

    return () => subscription.unsubscribe();
  }, [startValue, endValue, selectedChannelId, channelIds]);

  const channelOptions = useMemo(
    () => [{ label: '전체', value: '' }, ...channels.map((ch) => ({ label: ch.name, value: ch._id }))],
    [channels],
  );

  return (
    <div className={classes.container}>
      <TableOptions setDates={handleDatesChange} value={dates}>
        <div className='flexRow flexAlignCenter'>
          <span>메시지 수: {rowData.length}</span>
          {mode === 'broadcast' && (
            <Select
              style={{ width: 200 }}
              options={channelOptions}
              value={selectedChannelId ?? ''}
              onChange={handleChannelChange}
              placeholder='채널 필터'
            />
          )}
        </div>
      </TableOptions>
      <div className={classes.mainArea}>
        <div className={selectedMessageId ? classes.gridWrapperWithPanel : classes.gridWrapper}>
          <MessageTable rowData={rowData} onSelectMessage={setSelectedMessageId} />
        </div>
        {selectedMessageId && (
          <div className={classes.detailPanel}>
            <FcmStatusPanel messageId={selectedMessageId} onClose={() => setSelectedMessageId(null)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageHistory;
